import { useOutletContext } from "react-router-dom";
import { formatDistanceToNowStrict } from "date-fns";
import { mutate } from "swr";
import useToast from "~/components/ui/toast";
import { useFavorites } from "~/hooks/useFavorites";
import { AvatarStack } from "~/pages/manifold-nextgen/components/AvatarStack";
import { EditableDescription } from "~/pages/manifold-nextgen/components/EditableDescription";
import { EditableTitle } from "~/pages/manifold-nextgen/components/EditableTitle";
import {
  OverviewTile,
  OverviewTiles,
} from "~/pages/manifold-nextgen/components/EntityOverviewHeader";
import { FavoriteButton } from "~/pages/manifold-nextgen/components/FavoriteButton";
import { useAgentShare } from "~/pages/manifold-nextgen/hooks/useAgentShare";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import { useOwners } from "~/pages/manifold-nextgen/hooks/useOwners";
import {
  Agent,
  AgentAccessLevel,
  AgentScope,
  agentPath,
  mutateAgentList,
  updateAgent,
} from "~/services/agents";
import { AgentAccessBadge } from "./AgentAccessBadge";
import { AgentAvatar } from "./AgentAvatar";

interface AgentOverviewTabProps {
  // Overrides the outlet-context agent — used when this tab is rendered
  // outside the AgentDetail route tree (the project context sidebar).
  agent?: Agent;
  // True when this tab is rendered inside the session context sidebar,
  // which only ever shows a read-only view.
  sessionSidebarView?: boolean;
}

export const AgentOverviewTab = ({
  agent: agentProp,
  sessionSidebarView = false,
}: AgentOverviewTabProps) => {
  const outletAgent = useOutletContext<Agent>();
  const agent = agentProp ?? outletAgent;
  const canEditGrant = useCanEditAgent(agent);
  const isEditable = !sessionSidebarView && canEditGrant;
  const isSystemAgent = agent.scope === AgentScope.SYSTEM;
  const { showError } = useToast();

  const { collaborators, accessLevels } = useAgentShare(agent.id);
  const { isFavorite, toggleFavorite } = useFavorites("agent-favorites", [agent], (a) => a.id);

  const owners = useOwners(collaborators, accessLevels, AgentAccessLevel.OWNER);

  const tiles: OverviewTile[] = [
    {
      label: "My Role",
      // `access_level` is the caller's resolved level — at least USER for a SYSTEM
      // agent via its `user:*` grant, higher for a directly-granted collaborator.
      content: <AgentAccessBadge accessLevel={agent.access_level} />,
    },
    {
      label: "Owners",
      content: <AvatarStack items={owners} />,
    },
    {
      label: "Type",
      content: isSystemAgent ? "System" : "Custom",
    },
    {
      label: "Last Updated",
      content: agent.updated_at
        ? formatDistanceToNowStrict(new Date(agent.updated_at), {
            addSuffix: true,
          })
        : "—",
    },
  ];

  // Name edits commit immediately (inline pencil), same as Description below.
  const handleRename = async (next: string) => {
    try {
      await updateAgent(agent.id, { display_name: next });
      // Name drives the list label and its ordering, so refresh the lists too.
      await Promise.all([mutate(agentPath(agent.id)), mutateAgentList()]);
    } catch (e) {
      showError("Failed to rename agent");
      throw e;
    }
  };

  const handleSaveDescription = async (next: string) => {
    try {
      await updateAgent(agent.id, { description: next });
      // `description` shows on the list card, so refresh the lists too.
      await Promise.all([mutate(agentPath(agent.id)), mutateAgentList()]);
    } catch (e) {
      showError("Failed to update description");
      throw e;
    }
  };

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5">
      {!sessionSidebarView && (
        <div className="flex items-center gap-3">
          <AgentAvatar agent={agent} size={48} />
          <EditableTitle
            value={agent.display_name}
            canEdit={isEditable}
            label="Agent name"
            onSave={handleRename}
          />
          <FavoriteButton
            favorite={isFavorite(agent.id)}
            onToggle={() => toggleFavorite(agent.id)}
          />
        </div>
      )}

      <OverviewTiles tiles={tiles} />

      <EditableDescription
        value={agent.description}
        canEdit={isEditable}
        onSave={handleSaveDescription}
      />
    </div>
  );
};
